import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import DataTable from "@/components/DataTable";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Check, X, Trash2, Wand2, Pencil } from "lucide-react";
import { useAuth, can } from "@/context/AuthContext";
import { toast } from "sonner";
import CascadeDeleteDialog from "@/components/CascadeDeleteDialog";
import BulkOpsDialog from "@/components/BulkOpsDialog";

export default function OudersPage() {
  const { user } = useAuth();
  const canWrite = can(user, "parents", "write");
  const canDelete = can(user, "parents", "delete");
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(init());
  const [editingId, setEditingId] = useState(null);
  const [deleteFor, setDeleteFor] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [bulkMode, setBulkMode] = useState(null);

  const load = useCallback(async () => { const r = await api.get("/parents"); setRows(r.data); }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    try {
      if (editingId) {
        await api.put(`/parents/${editingId}`, form);
        toast.success("Ouder bijgewerkt");
      } else {
        await api.post("/parents", form);
        toast.success("Ouder toegevoegd");
      }
      setOpen(false); setForm(init()); setEditingId(null); load();
    }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const startEdit = (row) => {
    setForm({
      voornaam: row.voornaam || "",
      achternaam: row.achternaam || "",
      email: row.email || "",
      telefoon: row.telefoon || "",
      adres: row.adres || "",
      postcode: row.postcode || "",
      stad: row.stad || "",
      iban: row.iban || "",
      machtiging: !!row.machtiging,
    });
    setEditingId(row.id);
    setOpen(true);
  };

  const [detailOuder, setDetailOuder] = useState(null);
  const [linkedKids, setLinkedKids] = useState([]);

  const openDetail = async (ouder) => {
    setDetailOuder(ouder);
    setLinkedKids([]);
    try {
      const { data } = await api.get(`/children`);
      const own = (Array.isArray(data) ? data : data?.items || []).filter(k =>
        (k.parent_ids || []).includes(ouder.id)
      );
      setLinkedKids(own);
    } catch (_) { /* silent */ }
  };

  const columns = [
    { key: "achternaam", label: "Naam", render: r => (
      <div>
        <div className="font-medium text-neutral-800">{r.voornaam} {r.achternaam}</div>
        <div className="text-xs text-neutral-500">{r.stad || "—"}</div>
      </div>
    )},
    { key: "email", label: "E-mail", render: r => (
      r.email ? (
        <a
          href={`mailto:${r.email}`}
          onClick={(e) => e.stopPropagation()}
          className="text-sm text-[color:var(--sage)] hover:underline"
          data-testid={`ouder-email-${r.id}`}
        >{r.email}</a>
      ) : <span className="text-xs text-neutral-400">—</span>
    )},
    { key: "telefoon", label: "Telefoon", render: r => (
      r.telefoon ? (
        <a href={`tel:${r.telefoon}`} onClick={(e) => e.stopPropagation()} className="text-sm">
          {r.telefoon}
        </a>
      ) : <span className="text-xs text-neutral-400">—</span>
    )},
    { key: "adres", label: "Adres", render: r => (
      <div className="text-xs">
        <div>{r.adres || "—"}</div>
        {(r.postcode || r.stad) && <div className="text-neutral-500">{r.postcode} {r.stad}</div>}
      </div>
    )},
    { key: "iban", label: "IBAN", render: r => <span className="font-mono text-xs">{r.iban || "—"}</span> },
    { key: "machtiging", label: "Machtiging", render: r => (
      r.machtiging
        ? <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 border" variant="outline"><Check className="h-3 w-3 mr-1" /> Ja</Badge>
        : <Badge className="bg-red-50 text-red-700 border-red-200 border" variant="outline"><X className="h-3 w-3 mr-1" /> Nee</Badge>
    )},
  ];
  if (canWrite) {
    columns.push({
      key: "bewerken", label: "", sortable: false,
      render: r => (
        <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); startEdit(r); }}
          data-testid={`edit-ouder-${r.id}`}
          className="text-[color:var(--sage)] hover:bg-[color:var(--sage)]/10" title="Bewerk">
          <Pencil className="h-4 w-4" />
        </Button>
      ),
    });
  }
  if (canDelete) {
    columns.push({
      key: "verwijder", label: "", sortable: false,
      render: r => (
        <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); setDeleteFor(r); }}
          data-testid={`delete-ouder-${r.id}`}
          className="text-red-600 hover:text-red-700 hover:bg-red-50" title="Verwijder">
          <Trash2 className="h-4 w-4" />
        </Button>
      ),
    });
  }

  return (
    <div>
      <PageHeader
        title="Ouders"
        subtitle="Contact- en incassogegevens. Machtigingen zijn essentieel voor automatische incasso."
        testid="ouders-header"
        actions={canWrite && (
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditingId(null); setForm(init()); } }}>
            <DialogTrigger asChild>
              <Button data-testid="add-ouder-btn" style={{ background: "var(--sage)" }} className="text-white hover:brightness-110">
                <Plus className="h-4 w-4 mr-1" /> Nieuwe ouder
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-white max-w-lg">
              <DialogHeader>
                <DialogTitle>{editingId ? "Ouder bewerken" : "Nieuwe ouder toevoegen"}</DialogTitle>
                <DialogDescription>Vul de contact- en incassogegevens in.</DialogDescription>
              </DialogHeader>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Voornaam</Label><Input value={form.voornaam} onChange={e => setForm({ ...form, voornaam: e.target.value })} /></div>
                <div><Label>Achternaam</Label><Input value={form.achternaam} onChange={e => setForm({ ...form, achternaam: e.target.value })} /></div>
                <div><Label>E-mail</Label><Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} /></div>
                <div><Label>Telefoon</Label><Input value={form.telefoon} onChange={e => setForm({ ...form, telefoon: e.target.value })} /></div>
                <div className="col-span-2"><Label>Adres</Label><Input value={form.adres} onChange={e => setForm({ ...form, adres: e.target.value })} /></div>
                <div><Label>Postcode</Label><Input value={form.postcode} onChange={e => setForm({ ...form, postcode: e.target.value })} /></div>
                <div><Label>Stad</Label><Input value={form.stad} onChange={e => setForm({ ...form, stad: e.target.value })} /></div>
                <div className="col-span-2"><Label>IBAN</Label><Input value={form.iban} onChange={e => setForm({ ...form, iban: e.target.value })} placeholder="NLxx BANK xxxxxxxxxx" /></div>
                <div className="col-span-2">
                  <label className="flex items-center gap-2 text-sm">
                    <Checkbox checked={form.machtiging} onCheckedChange={v => setForm({ ...form, machtiging: !!v })} />
                    Machtiging voor automatische incasso ontvangen
                  </label>
                </div>
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={() => { setOpen(false); setEditingId(null); setForm(init()); }}>Annuleren</Button>
                <Button onClick={submit} data-testid="ouder-save-btn" style={{ background: "var(--sage)" }} className="text-white">Opslaan</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      />
      {selected.size > 0 && (
        <div className="mb-3 flex items-center gap-2 p-2.5 bg-[color:var(--sage-100)]/40 border border-[color:var(--sage-100)] rounded-md" data-testid="ouders-bulk-toolbar">
          <span className="text-sm font-medium text-[color:var(--sage)]" data-testid="ouders-bulk-count">{selected.size} geselecteerd</span>
          <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())} data-testid="ouders-bulk-clear">Selectie wissen</Button>
          <div className="flex-1" />
          {canWrite && (
            <Button size="sm" onClick={() => setBulkMode("update")} data-testid="ouders-bulk-update"
                    style={{ background: "var(--sage)" }} className="text-white">
              <Wand2 className="h-3.5 w-3.5 mr-1.5" /> Wijzig {selected.size}
            </Button>
          )}
          {canDelete && (
            <Button size="sm" onClick={() => setBulkMode("delete")} data-testid="ouders-bulk-delete"
                    className="bg-red-600 hover:bg-red-700 text-white">
              <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Verwijder {selected.size}
            </Button>
          )}
        </div>
      )}
      <DataTable columns={columns} rows={rows} testid="ouders-table"
                 onRowClick={openDetail}
                 selectable={canWrite || canDelete} selected={selected} onSelectionChange={setSelected} />
      <CascadeDeleteDialog
        open={!!deleteFor}
        onClose={() => setDeleteFor(null)}
        resource="parents"
        resourceId={deleteFor?.id}
        resourceLabel={deleteFor ? `${deleteFor.voornaam} ${deleteFor.achternaam}` : ""}
        onDeleted={load}
      />
      <BulkOpsDialog
        open={!!bulkMode}
        onClose={() => setBulkMode(null)}
        mode={bulkMode}
        resource="parents"
        ids={[...selected]}
        onDone={() => { setSelected(new Set()); load(); }}
        updateFields={[          { key: "archived", label: "Status", type: "boolean",
            trueLabel: "Archiveren", falseLabel: "Activeren",
            hint: "Soft-status: gearchiveerd behoudt data maar verbergt in dagelijkse lijsten." },
        ]}
      />
      {/* Detail-dialog: alle info van deze ouder + gekoppelde kinderen */}
      <Dialog open={!!detailOuder} onOpenChange={(v) => { if (!v) setDetailOuder(null); }}>
        <DialogContent className="max-w-lg bg-white" data-testid="ouder-detail-dialog">
          <DialogHeader>
            <DialogTitle>{detailOuder?.voornaam} {detailOuder?.achternaam}</DialogTitle>
            <DialogDescription>Volledig ouder-profiel</DialogDescription>
          </DialogHeader>
          {detailOuder && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-[110px_1fr] gap-y-2 gap-x-3">
                <div className="text-neutral-500">E-mail</div>
                <div data-testid="ouder-detail-email">
                  {detailOuder.email ? (
                    <a href={`mailto:${detailOuder.email}`} className="text-[color:var(--sage)] hover:underline">
                      {detailOuder.email}
                    </a>
                  ) : "—"}
                </div>
                <div className="text-neutral-500">Telefoon</div>
                <div>{detailOuder.telefoon ? <a href={`tel:${detailOuder.telefoon}`}>{detailOuder.telefoon}</a> : "—"}</div>
                <div className="text-neutral-500">Adres</div>
                <div>
                  <div>{detailOuder.adres || "—"}</div>
                  {(detailOuder.postcode || detailOuder.stad) && (
                    <div className="text-neutral-500 text-xs">{detailOuder.postcode} {detailOuder.stad}</div>
                  )}
                </div>
                <div className="text-neutral-500">IBAN</div>
                <div className="font-mono text-xs">{detailOuder.iban || "—"}</div>
                <div className="text-neutral-500">Machtiging</div>
                <div>{detailOuder.machtiging
                  ? <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 border" variant="outline"><Check className="h-3 w-3 mr-1" /> Ja</Badge>
                  : <Badge className="bg-red-50 text-red-700 border-red-200 border" variant="outline"><X className="h-3 w-3 mr-1" /> Nee</Badge>}
                </div>
              </div>
              <div className="pt-3 border-t border-neutral-100">
                <div className="text-xs font-medium text-neutral-500 uppercase tracking-wider mb-2">
                  Kinderen ({linkedKids.length})
                </div>
                {linkedKids.length === 0 ? (
                  <div className="text-xs text-neutral-400">Geen gekoppelde kinderen</div>
                ) : (
                  <div className="space-y-1.5">
                    {linkedKids.map(k => (
                      <div key={k.id} className="flex items-center justify-between text-xs bg-neutral-50 rounded px-2 py-1.5">
                        <span className="font-medium">{k.voornaam} {k.achternaam}</span>
                        <span className="text-neutral-500">{k.geboortedatum}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDetailOuder(null)} data-testid="ouder-detail-close">
              Sluiten
            </Button>
            {canWrite && detailOuder && (
              <Button
                onClick={() => { const o = detailOuder; setDetailOuder(null); startEdit(o); }}
                data-testid="ouder-detail-edit"
                style={{ background: "var(--sage)" }} className="text-white"
              >
                <Pencil className="h-4 w-4 mr-1" /> Bewerken
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function init() {
  return { voornaam: "", achternaam: "", email: "", telefoon: "", adres: "", postcode: "", stad: "", iban: "", machtiging: false };
}
